import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@suscri/ui';
import { ArrowRight, KeyRound, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthShell,
  authErrorClass,
  authInputClass,
  authLabelClass,
} from '../components/AuthShell';
import { useAuthStore } from '../lib/auth';
import { trpc } from '../lib/trpc';
import { getLandingUrl } from '../lib/urls';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const loginMutation = trpc.auth.login.useMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const result = await loginMutation.mutateAsync(data);
      setAuth(result.user, result.token);
      navigate('/dashboard');
    } catch (error) {
      setError('root', {
        message: error instanceof Error ? error.message : t('Error al iniciar sesión'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      compact
      title={t('Iniciar sesión')}
      subtitle={
        <>
          {t('¿Aún no tienes cuenta?')}{' '}
          <Link to="/register" className="font-semibold text-primary hover:text-primary/80">
            {t('Crear una cuenta nueva')}
          </Link>
        </>
      }
      footer={
        <a href={getLandingUrl()} className="font-medium hover:text-foreground">
          {t('Volver a la página principal')}
        </a>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div>
          <label htmlFor="email" className={authLabelClass}>Email</label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              {...register('email')}
              id="email"
              type="email"
              autoComplete="email"
              className={`${authInputClass} pl-10`}
              placeholder="tu@email.com"
              aria-invalid={Boolean(errors.email)}
            />
          </div>
          {errors.email && <p className={authErrorClass}>{errors.email.message}</p>}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between gap-4">
            <label htmlFor="password" className="text-sm font-semibold text-foreground">{t('Contraseña')}</label>
            <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:text-primary/80">
              {t('¿Olvidaste tu contraseña?')}
            </Link>
          </div>
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              {...register('password')}
              id="password"
              type="password"
              autoComplete="current-password"
              className={`${authInputClass} pl-10`}
              placeholder="••••••••"
              aria-invalid={Boolean(errors.password)}
            />
          </div>
          {errors.password && <p className={authErrorClass}>{errors.password.message}</p>}
        </div>

        {errors.root && (
          <div className="rounded-xl border border-danger/25 bg-danger/10 p-3.5 text-sm font-medium text-danger" role="alert">
            {errors.root.message}
          </div>
        )}

        <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
          {isLoading ? t('Iniciando sesión...') : t('Iniciar sesión')}
          {!isLoading && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
        </Button>

        <div className="rounded-xl border border-primary/20 bg-primary/[0.07] p-3.5 text-center text-xs text-muted-foreground">
          <span className="font-semibold text-primary">{t('Usuario demo:')}</span>{' '}
          demo@acme.com / Demo1234!
        </div>
      </form>
    </AuthShell>
  );
}
